import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import GenericHeader from "../../../Componentes/GenericHeader";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import { formatarData } from "../../../Funcoes/FormatarData";
import useApi from "../../../hooks/useApi";
import { useModal } from "../../../contexts/ModalContext";
import ModalViewAtendimento from "./ModalViewAtendimento";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const Statistics = styled.div`
    width: 280px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ContainerStatistic = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 100%;
`;

const TituloStatistic = styled.h3`
    text-align: center;
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: bold;
    color: #000;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

/** Mesmo critério do legado: ORDER BY status ASC, id DESC */
function sortByStatusThenIdDesc(list) {
    return [...list].sort((a, b) => {
        const statusCmp = String(a.status ?? "").localeCompare(
            String(b.status ?? ""),
            "pt-BR",
            { sensitivity: "base" }
        );
        if (statusCmp !== 0) return statusCmp;
        return Number(b.id) - Number(a.id);
    });
}

function uniqueSortedStrings(values) {
    return [...new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
}

function startOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T00:00:00`).getTime();
}

function endOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T23:59:59.999`).getTime();
}

function formatarTempo(segundos) {
    if (segundos == null || segundos === "") return "-";

    const total = Number(segundos);
    if (!Number.isFinite(total)) return "-";

    const horas = Math.floor(total / 3600);
    const minutos = Math.floor((total % 3600) / 60);
    const seg = total % 60;

    return [horas, minutos, seg]
        .map((n) => String(n).padStart(2, "0"))
        .join(":");
}

function isSameDay(dateValue, reference = new Date()) {
    if (!dateValue) return false;

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return false;

    return (
        date.getFullYear() === reference.getFullYear() &&
        date.getMonth() === reference.getMonth() &&
        date.getDate() === reference.getDate()
    );
}

function calcularEstatisticas(lista) {
    const tempoAcumulado = lista.reduce((acc, row) => {
        const segundos = Number(row.segundos);
        return Number.isFinite(segundos) ? acc + segundos : acc;
    }, 0);

    const atendimentosHoje = lista.filter((row) => isSameDay(row.data_inicio));
    const hojeComTempo = atendimentosHoje.filter((row) => Number.isFinite(Number(row.segundos)));

    const tempoMedio =
        hojeComTempo.length > 0
            ? Math.round(
                  hojeComTempo.reduce((acc, row) => acc + Number(row.segundos), 0) /
                      hojeComTempo.length
              )
            : null;

    return {
        geral: {
            total: lista.length,
            tempoAcumulado,
        },
        diario: {
            total: atendimentosHoje.length,
            tempoMedio,
        },
    };
}

function formatarDataAtendimento(dataInicio) {
    if (!dataInicio) return "-";
    try {
        return formatarData(dataInicio);
    } catch {
        return "-";
    }
}

/** MultiselectCheck armazena valores primitivos (strings), não objetos { value, label } */
function toValueSet(selected) {
    if (!Array.isArray(selected) || selected.length === 0) return null;
    return new Set(
        selected.map((item) =>
            item != null && typeof item === "object" && "value" in item ? item.value : item
        )
    );
}

function aplicarFiltros(lista, filtros) {
    const nomeBusca = filtros.nomeColaborador.trim().toUpperCase();
    const matBusca = filtros.matriculaColaborador.trim().toUpperCase();

    const tDe = startOfDayMs(filtros.dataInicioDe);
    const tAte = endOfDayMs(filtros.dataInicioAte);

    const funcoesSet = toValueSet(filtros.selectedFuncoes);
    const centrosSet = toValueSet(filtros.selectedCentrosCusto);
    const queixasSet = toValueSet(filtros.selectedQueixas);
    const statusSet = toValueSet(filtros.selectedStatuses);

    return lista.filter((row) => {
        const nome = String(row.nome ?? "").toUpperCase();
        const mat = String(row.matricula ?? "").toUpperCase();

        if (nomeBusca && !nome.includes(nomeBusca)) return false;
        if (matBusca && !mat.includes(matBusca)) return false;

        if (funcoesSet && !funcoesSet.has(String(row.funcao ?? ""))) return false;
        if (centrosSet && !centrosSet.has(String(row.centro_custo ?? ""))) return false;
        if (queixasSet && !queixasSet.has(String(row.queixa ?? ""))) return false;
        if (statusSet && !statusSet.has(String(row.status ?? ""))) return false;

        if (row.data_inicio) {
            const t = new Date(row.data_inicio).getTime();
            if (tDe != null && t < tDe) return false;
            if (tAte != null && t > tAte) return false;
        } else {
            if (tDe != null || tAte != null) return false;
        }

        return true;
    });
}

const Content = () => {
    const api = useApi();
    const { openModal, closeModal, isModalOpen } = useModal();
    const tableRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [atendimentos, setAtendimentos] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const [nomeColaborador, setNomeColaborador] = useState("");
    const [matriculaColaborador, setMatriculaColaborador] = useState("");
    const [selectedFuncoes, setSelectedFuncoes] = useState([]);
    const [selectedCentrosCusto, setSelectedCentrosCusto] = useState([]);
    const [selectedQueixas, setSelectedQueixas] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [dataInicioDe, setDataInicioDe] = useState("");
    const [dataInicioAte, setDataInicioAte] = useState("");
    const [selectedAtendimentoId, setSelectedAtendimentoId] = useState(null);

    const filtros = useMemo(
        () => ({
            nomeColaborador,
            matriculaColaborador,
            selectedFuncoes,
            selectedCentrosCusto,
            selectedQueixas,
            selectedStatuses,
            dataInicioDe,
            dataInicioAte,
        }),
        [
            nomeColaborador,
            matriculaColaborador,
            selectedFuncoes,
            selectedCentrosCusto,
            selectedQueixas,
            selectedStatuses,
            dataInicioDe,
            dataInicioAte,
        ]
    );

    const filteredAtendimentos = useMemo(
        () => aplicarFiltros(atendimentos, filtros),
        [atendimentos, filtros]
    );

    const estatisticas = useMemo(
        () => calcularEstatisticas(filteredAtendimentos),
        [filteredAtendimentos]
    );

    const opcoesFuncao = useMemo(() => uniqueSortedStrings(atendimentos.map((r) => r.funcao)), [atendimentos]);
    const opcoesCentroCusto = useMemo(
        () => uniqueSortedStrings(atendimentos.map((r) => r.centro_custo)),
        [atendimentos]
    );
    const opcoesQueixa = useMemo(() => uniqueSortedStrings(atendimentos.map((r) => r.queixa)), [atendimentos]);

    const opcoesFuncaoMs = useMemo(
        () => opcoesFuncao.map((opt) => ({ value: opt, label: opt })),
        [opcoesFuncao]
    );
    const opcoesCentroCustoMs = useMemo(
        () => opcoesCentroCusto.map((opt) => ({ value: opt, label: opt })),
        [opcoesCentroCusto]
    );
    const opcoesQueixaMs = useMemo(
        () => opcoesQueixa.map((opt) => ({ value: opt, label: opt })),
        [opcoesQueixa]
    );

    const opcoesStatusMs = useMemo(
        () => [
            { value: "EM ANDAMENTO", label: "EM ANDAMENTO" },
            { value: "FINALIZADO", label: "FINALIZADO" },
        ],
        []
    );

    const initialFetch = async () => {
        setLoading(true);

        try {
            const { data } = await api.get("/Painel/API/AtendimentoAmb/List/index.php");
            const lista = Array.isArray(data) ? data : [];

            setAtendimentos(sortByStatusThenIdDesc(lista));
        } catch (error) {
            console.error(error);
            setAtendimentos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filteredAtendimentos]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = filteredAtendimentos.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filteredAtendimentos.length]);

    const columns = useMemo(
        () => [
            {
                id: "id",
                header: "ID",
                accessor: "id",
                sortable: true,
                width: "70px",
                align: "center",
            },
            {
                id: "nome",
                header: "NOME",
                accessor: "nome",
                sortable: true,
            },
            {
                id: "matricula",
                header: "MATRÍCULA",
                accessor: "matricula",
                sortable: true,
                width: "110px",
            },
            {
                id: "funcao",
                header: "FUNÇÃO",
                accessor: "funcao",
                sortable: true,
            },
            {
                id: "centro_custo",
                header: "CENTRO CUSTO",
                accessor: "centro_custo",
                sortable: true,
            },
            {
                id: "queixa",
                header: "QUEIXA / OCORRÊNCIA",
                accessor: "queixa",
                sortable: true,
            },
            {
                id: "data_inicio",
                header: "DATA",
                accessor: "data_inicio",
                sortable: true,
                width: "120px",
                render: (_value, row) => formatarDataAtendimento(row.data_inicio),
                sortValue: (row) => row.data_inicio ?? "",
            },
            {
                id: "segundos",
                header: "TEMPO",
                accessor: "segundos",
                sortable: true,
                width: "100px",
                align: "center",
                render: (value) => formatarTempo(value),
            },
            {
                id: "internacao",
                header: "INTERNAÇÃO",
                accessor: "internacao",
                sortable: true,
                width: "120px",
                align: "center",
                render: (value) => value ?? "-",
            },
            {
                id: "status",
                header: "STATUS",
                accessor: "status",
                sortable: true,
                width: "140px",
                sortValue: (row) => {
                    const st = row.status ?? "";
                    const id = Number(row.id) || 0;
                    const statusRank =
                        st === "EM ANDAMENTO" ? 0 : st === "FINALIZADO" ? 1 : 2;
                    return statusRank * 1e10 + (999999999 - id);
                },
            },
        ],
        []
    );

    const abrirVisualizacao = (row) => {
        if (!row?.id) return;
        setSelectedAtendimentoId(row.id);
        openModal("atendimentoAmbView");
    };

    const fecharVisualizacao = () => {
        setSelectedAtendimentoId(null);
        closeModal();
    };

    return (
        <>
        <PageContent>
            <GenericHeader responsive>
                <Filters columns={4}>
                    <FilterContainer
                        type="text"
                        placeholder="NOME COLABORADOR"
                        valor={nomeColaborador}
                        changeFunc={setNomeColaborador}
                    />
                    <FilterContainer
                        type="text"
                        placeholder="MATRÍCULA"
                        valor={matriculaColaborador}
                        changeFunc={setMatriculaColaborador}
                        maxLength={6}
                    />
                    <MultiselectCheck
                        options={opcoesFuncaoMs}
                        selectedOptions={selectedFuncoes}
                        setSelectedOptions={setSelectedFuncoes}
                        placeholder="FUNÇÃO..."
                    />
                    <MultiselectCheck
                        options={opcoesCentroCustoMs}
                        selectedOptions={selectedCentrosCusto}
                        setSelectedOptions={setSelectedCentrosCusto}
                        placeholder="CENTRO CUSTO..."
                    />
                    <MultiselectCheck
                        options={opcoesQueixaMs}
                        selectedOptions={selectedQueixas}
                        setSelectedOptions={setSelectedQueixas}
                        placeholder="QUEIXA / OCORRÊNCIA..."
                    />
                    <MultiselectCheck
                        options={opcoesStatusMs}
                        selectedOptions={selectedStatuses}
                        setSelectedOptions={setSelectedStatuses}
                        placeholder="STATUS..."
                    />
                    <FilterContainer
                        type="date"
                        placeholder="DATA INÍCIO — A PARTIR DE"
                        valor={dataInicioDe}
                        changeFunc={setDataInicioDe}
                    />
                    <FilterContainer
                        type="date"
                        placeholder="DATA INÍCIO — ATÉ"
                        valor={dataInicioAte}
                        changeFunc={setDataInicioAte}
                    />
                </Filters>

                <Statistics>
                    <ContainerStatistic>
                        <TituloStatistic>ESTATÍSTICAS GERAIS</TituloStatistic>
                        <TextoStatistic>
                            ATENDIMENTOS REALIZADOS: {estatisticas.geral.total}
                        </TextoStatistic>
                        <TextoStatistic>
                            TEMPO ACUMULADO: {formatarTempo(estatisticas.geral.tempoAcumulado)}
                        </TextoStatistic>
                    </ContainerStatistic>
                </Statistics>

                <Statistics>
                    <ContainerStatistic>
                        <TituloStatistic>ESTATÍSTICAS DIÁRIAS</TituloStatistic>
                        <TextoStatistic>
                            ATENDIMENTOS REALIZADOS: {estatisticas.diario.total}
                        </TextoStatistic>
                        <TextoStatistic>
                            TEMPO MÉDIO:{" "}
                            {estatisticas.diario.tempoMedio != null
                                ? formatarTempo(estatisticas.diario.tempoMedio)
                                : "-"}
                        </TextoStatistic>
                    </ContainerStatistic>
                </Statistics>
            </GenericHeader>

            <TableFlexFill>
                <SortableTable
                    ref={tableRef}
                    columns={columns}
                    data={filteredAtendimentos}
                    limit={visibleCount}
                    onScrollReachEnd={loadMoreRows}
                    scrollReachThreshold={SCROLL_THRESHOLD}
                    isLoading={loading}
                    loadingText="Carregando atendimentos..."
                    emptyText="Nenhum atendimento encontrado."
                    initialSort={{ key: "status", direction: "asc" }}
                    getRowId={(row) => row.id}
                    getRowProps={(row) =>
                        row.status === "EM ANDAMENTO"
                            ? {
                                  style: {
                                      fontWeight: "bold",
                                      color: "#c00",
                                  },
                              }
                            : null
                    }
                    onRowClick={abrirVisualizacao}
                />
            </TableFlexFill>
        </PageContent>

        {isModalOpen("atendimentoAmbView") && selectedAtendimentoId != null && (
            <ModalViewAtendimento
                atendimentoId={selectedAtendimentoId}
                fechar={fecharVisualizacao}
            />
        )}
        </>
    );
};

export default Content;
